/**
 * Target selection and positioning (PLAN §7 "Movement"):
 *   - the tank follows the dungeon route, waits for the group, pulls the next pack,
 *     picks up loose mobs and turns frontal mobs/bosses away from the group;
 *   - melee stand behind their target; ranged/healer keep 15–25 yd behind the tank;
 *   - everyone follows the tank between pulls. With a human tank, bots follow the human
 *     and assist their target.
 * The "pull axis" points from the group toward the enemies: from the previous route
 * stop to the current pack (bot tank) or from the tank to the enemies (human tank).
 */
import type { Command, PackDef, Vec2 } from '@keystone/sim';
import { add, angleDiff, angleOf, centroid, dist, fromAngle, norm, perp, scale, sub } from './geometry';
import type { ClassKit } from './kits';
import type { BotMemory } from './memory';
import { inMelee, isBehind, meleeRange, packAlive, packCenter, simWorld, type DangerArea, type Perception, type RUnit } from './perception';
import { inDanger, safePoint } from './awareness/dodge';
import { liveCenter, restOfPack, roamPlan, unitAlive } from './patrols'; // +patrols

export const RANGED_MIN = 15;
export const RANGED_MAX = 25;
const RANGED_DIST = 20;
/** Where sloppy ranged (skill dial) stand: close behind the tank, all on one spot. */
const SLOPPY_RANGED_DIST = 8;
const CAST_RANGE = 38;
const PULL_STAGING_GAP = 10;
const MAX_PULL_WAIT_MS = 12000;
/** +personas: how long a bot tank waits for an AFK / wandering member before pulling anyway. */
export const AWAY_WAIT_MS = 30_000;

export interface RouteContext {
  pack: PackDef | null;
  center: Vec2 | null;
  /** Unit vector from the previous route stop to the current pack. */
  axis: Vec2;
  /** +patrols: the previous route stop (or the entrance). */
  from?: Vec2;
}

export function routeContext(p: Perception): RouteContext {
  const d = p.dungeon;
  let prev: Vec2 = d?.entrance ?? p.self.pos;
  if (d) {
    for (const id of d.route) {
      const c = packCenter(d, id);
      if (!c) continue;
      if (packAlive(p.world, id)) {
        const pack = d.packs.find((x) => x.id === id) ?? null;
        // +patrols: a patrol on the route is where it walks; a split loose pack is where its rest stands.
        const live = pack && (pack.path || pack.link === 'loose') ? (liveCenter(p, pack) ?? c) : c;
        return { pack, center: live, axis: norm(sub(live, prev), fromAngle(d.entranceFacing)), from: prev };
      }
      prev = c;
    }
  }
  return { pack: null, center: null, axis: fromAngle(p.tank?.facing ?? p.self.facing) };
}

/** Direction from the group toward the fight. */
export function combatAxis(p: Perception): Vec2 {
  const tank = p.tank;
  const enemyCenter = centroid(p.engaged.map((e) => e.pos));
  if (tank?.alive && tank.controller !== 'bot' && enemyCenter) return norm(sub(enemyCenter, tank.pos), fromAngle(tank.facing));
  const rc = routeContext(p);
  if (rc.center) return rc.axis;
  return enemyCenter && tank ? norm(sub(enemyCenter, tank.pos)) : fromAngle(p.self.facing);
}

// ---------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------

/** Engaged enemy attacking someone other than me (tank's taunt candidate). */
export function looseMob(p: Perception): RUnit | undefined {
  const self = p.self;
  let best: RUnit | undefined;
  for (const e of p.engaged) {
    const victim = e.targetId ? p.world.units[e.targetId] : undefined;
    if (!victim || victim.id === self.id || victim.faction !== self.faction || !victim.alive) continue;
    if (!best || dist(e.pos, self.pos) < dist(best.pos, self.pos)) best = e;
  }
  return best;
}

function nearest(units: readonly RUnit[], to: Vec2): RUnit | undefined {
  let best: RUnit | undefined;
  for (const u of units) if (!best || dist(u.pos, to) < dist(best.pos, to)) best = u;
  return best;
}

/** Enemy this bot should be hitting (undefined when there is nothing to fight). */
export function chooseTarget(p: Perception, kit: ClassKit, mem?: BotMemory): RUnit | undefined {
  const self = p.self;
  const current = self.targetId ? p.world.units[self.targetId] : undefined;
  const engagedCurrent = current && p.engaged.includes(current) ? current : undefined;
  if (kit.role === 'tank' && self.controller === 'bot') {
    if (p.engaged.length === 0) {
      // +patrols: a roamer the tank decided to pull first.
      const o = mem ? unitAlive(p, mem.pullOverride) : undefined;
      if (o) return dist(o.pos, self.pos) <= 12 ? o : undefined;
      return pullTarget(p);
    }
    return looseMob(p) ?? engagedCurrent ?? nearest(p.engaged, self.pos);
  }
  if (p.engaged.length === 0) return undefined;
  const tank = p.tank?.alive ? p.tank : undefined;
  const tankTarget = tank?.targetId ? p.world.units[tank.targetId] : undefined;
  if (tank && tank.id !== self.id && tankTarget && p.engaged.includes(tankTarget)) return tankTarget;
  return engagedCurrent ?? nearest(p.engaged, (tank ?? self).pos);
}

/** Out of combat, the bot tank opens on the nearest member of the route pack once close. */
function pullTarget(p: Perception): RUnit | undefined {
  const rc = routeContext(p);
  if (!rc.pack) return undefined;
  const members = (p.world.packs[rc.pack.id]?.memberIds ?? []).map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive);
  const n = nearest(members, p.self.pos);
  return n && dist(n.pos, p.self.pos) <= 12 ? n : undefined;
}

// ---------------------------------------------------------------------------
// Positioning
// ---------------------------------------------------------------------------

function moveTo(p: Perception, mem: BotMemory, dest: Vec2, dangers: readonly DangerArea[]): Command[] {
  let to = dest;
  if (inDanger(p, dangers, to, p.self.radius)) {
    if (inDanger(p, dangers, p.self.pos, p.self.radius)) return [];
    const alt = safePoint(p, dangers, to, p.self.pos);
    if (!alt) return [];
    to = alt;
  }
  // Don't walk through something on the way (straight-line check; paths hug walls rarely).
  if (!inDanger(p, dangers, p.self.pos, p.self.radius) && crossesDanger(p, dangers, p.self.pos, to)) {
    if (p.self.move) return [{ type: 'stop', actorId: p.self.id }];
    return [];
  }
  const last = mem.lastMoveDest;
  if (last && p.self.move && dist(last, to) < 1.5) return [];
  mem.lastMoveDest = to;
  return [{ type: 'move', actorId: p.self.id, dest: to }];
}

function crossesDanger(p: Perception, dangers: readonly DangerArea[], from: Vec2, to: Vec2): boolean {
  if (dangers.length === 0) return false;
  const steps = Math.ceil(dist(from, to));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    if (inDanger(p, dangers, { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t }, p.self.radius)) return true;
  }
  return false;
}

function hold(p: Perception, mem: BotMemory): Command[] {
  mem.lastMoveDest = null;
  return [];
}

function hasFrontal(p: Perception, e: RUnit): boolean {
  if (e.tier === 'boss' || e.tier === 'miniboss') return true;
  const def = e.npcId ? p.view.content.npc(e.npcId) : undefined;
  return (def?.abilities ?? []).some((id) => {
    const ab = p.view.content.enemyAbility(id);
    return ab?.kind === 'frontal' || ab?.shape?.type === 'cone' || ab?.shape?.type === 'line';
  });
}

/** Index of `self` among party members sharing a predicate (stable lateral spread). */
function slotAmong(p: Perception, pred: (u: RUnit) => boolean): number {
  return Math.max(0, p.partyAll.filter(pred).findIndex((u) => u.id === p.self.id));
}

const LATERAL = [0, 6, -6, 10, -10];

export function planPosition(p: Perception, kit: ClassKit, mem: BotMemory, target: RUnit | undefined, dangers: readonly DangerArea[]): Command[] {
  const self = p.self;
  const busyCasting = !!self.cast && !p.view.content.ability(self.cast.abilityId)?.canCastWhileMoving;
  const inCombat = p.engaged.length > 0;

  if (kit.role === 'tank' && self.controller === 'bot') {
    if (!inCombat) return planPull(p, mem, dangers);
    mem.pullWaitSince = null;
    mem.pullOverride = null;
    // +patrols: low keys pull a loose pack whole: drag the fight into the clump still standing.
    const rest = restOfPack(p, routeContext(p).pack);
    if (rest && self.hp / self.maxHp > 0.6 && dist(self.pos, rest.pos) > routeContext(p).pack!.aggroRadius - 3) return moveTo(p, mem, rest.pos, dangers);
    if (!target) return hold(p, mem);
    const range = meleeRange(self, target);
    const axis = combatAxis(p);
    const awayOk = Math.abs(angleDiff(angleOf(sub(self.pos, target.pos)), angleOf(axis))) < (50 * Math.PI) / 180;
    if (hasFrontal(p, target)) {
      if (inMelee(self, target) && awayOk) return hold(p, mem);
      return moveTo(p, mem, add(target.pos, scale(axis, range * 0.6)), dangers);
    }
    if (inMelee(self, target)) return hold(p, mem);
    return moveTo(p, mem, add(target.pos, scale(norm(sub(self.pos, target.pos)), range * 0.6)), dangers);
  }

  const tank = p.tank?.alive && p.tank.id !== self.id ? p.tank : undefined;

  if (inCombat && target) {
    if (!kit.ranged) {
      const range = meleeRange(self, target);
      const tanking = target.targetId === self.id;
      // Sloppy melee (skill dial) stand wherever they reached melee, front arcs included.
      const sloppy = mem.skill.positioning < 0.5;
      if (inMelee(self, target) && (tanking || sloppy || isBehind(self, target) || target.moving)) return hold(p, mem);
      if (busyCasting && inMelee(self, target)) return hold(p, mem);
      const behind = add(target.pos, scale(fromAngle(target.facing), -range * 0.6));
      return moveTo(p, mem, tanking || sloppy || target.moving ? add(target.pos, scale(norm(sub(self.pos, target.pos)), range * 0.6)) : behind, dangers);
    }
    const anchor = tank ?? target;
    const axis = combatAxis(p);
    const slot = slotAmong(p, (u) => u.role !== 'tank' && (u.classId ? p.view.content.class(u.classId)?.melee === null : false));
    // Sloppy ranged (skill dial) hug the pack and clump on one spot instead of spreading out.
    const sloppy = mem.skill.positioning < 0.5;
    const back = sloppy ? SLOPPY_RANGED_DIST : RANGED_DIST;
    const spot = clampToArena(p, add(add(anchor.pos, scale(axis, -back)), scale(perp(axis), sloppy ? 0 : (LATERAL[slot % LATERAL.length] ?? 0))));
    const dTank = dist(self.pos, anchor.pos);
    const offAxis = Math.abs(angleDiff(angleOf(sub(self.pos, anchor.pos)), angleOf(scale(axis, -1))));
    const behindOk = sloppy || offAxis < Math.PI / 3;
    const targetOk = dist(self.pos, target.pos) <= CAST_RANGE;
    if (busyCasting && targetOk && dTank <= RANGED_MAX + 10) return hold(p, mem);
    if (tank && dTank >= (sloppy ? 4 : RANGED_MIN) && dTank <= (sloppy ? SLOPPY_RANGED_DIST + 4 : RANGED_MAX) && behindOk && targetOk) return hold(p, mem);
    // Already settled (no move order) somewhere reasonable: don't chase every small turn of the axis;
    // a ranged player keeps casting until it really drifts off (frontals are dodged by the danger layer).
    if (tank && !self.move && targetOk && dTank >= RANGED_MIN - 3 && dTank <= RANGED_MAX + 5 && offAxis < (Math.PI * 5) / 9) return hold(p, mem);
    if (!tank && targetOk && dTank >= 8) return hold(p, mem);
    return moveTo(p, mem, spot, dangers);
  }

  // Out of combat (or nothing to hit): follow the tank, or the first other party member.
  const leader = tank ?? p.party.find((u) => u.id !== self.id);
  if (!leader) return hold(p, mem);
  const heading = leader.move?.mode === 'dest' ? norm(sub(leader.move.dest, leader.pos), fromAngle(leader.facing)) : fromAngle(leader.facing);
  const back = kit.ranged ? 7 : 3.5;
  const slot = slotAmong(p, (u) => u.id !== leader.id);
  const spot = add(add(leader.pos, scale(heading, -back)), scale(perp(heading), (slot - 1) * 3));
  if (dist(self.pos, spot) <= 4 && !leader.moving) return hold(p, mem);
  if (dist(self.pos, spot) <= 2) return hold(p, mem);
  return moveTo(p, mem, spot, dangers);
}

const ARENA_MARGIN = 3;

/** While a boss encounter is active, keep a spot inside its arena (never walk out the door). */
export function clampToArena(p: Perception, at: Vec2): Vec2 {
  const enc = p.world.encounter;
  const a = enc ? p.view.content.encounter(enc.encounterId)?.arena : undefined;
  if (!a) return at;
  const m = Math.min(ARENA_MARGIN, a.w / 2, a.h / 2);
  return { x: Math.min(a.x + a.w - m, Math.max(a.x + m, at.x)), y: Math.min(a.y + a.h - m, Math.max(a.y + m, at.y)) };
}

/** Bot tank between pulls: walk to a staging point, wait for the group, then pull. */
function planPull(p: Perception, mem: BotMemory, dangers: readonly DangerArea[]): Command[] {
  const rc = routeContext(p);
  if (!rc.pack || !rc.center) return hold(p, mem);
  const self = p.self;
  // +patrols: roamers first: pull one that comes at us clear of the packs, or wait for a patrol to pass.
  const roam = rc.pack.encounterId ? null : roamPlan(p, mem, rc.pack, rc.center);
  if (roam?.kind === 'pull') {
    mem.pullOverride = roam.target.id;
    return moveTo(p, mem, roam.target.pos, dangers);
  }
  mem.pullOverride = null;
  // +dungeon2: in enclosed layouts the ideal staging point can fall inside a wall (a pack just
  // past a doorway); slide it toward the pack until it stands on walkable floor.
  let back = rc.pack.aggroRadius + PULL_STAGING_GAP;
  let staging = sub(rc.center, scale(rc.axis, back));
  while (back > 2 && !p.view.queries.spatial.isWalkable(simWorld(p), staging)) {
    back -= 1;
    staging = sub(rc.center, scale(rc.axis, back));
  }
  // Hysteresis: once staged, only walk back to staging when well outside it. Walking to a pack
  // member that stands away from the pack centre (a boss that reset elsewhere after a wipe) used to
  // cross the threshold and flip between "stage" and "pull" forever.
  const stageR = rc.pack.aggroRadius + PULL_STAGING_GAP - 1 + (mem.pullWaitSince !== null ? PULL_STAGING_GAP : 0);
  if (dist(self.pos, staging) > 3 && dist(self.pos, rc.center) > stageR) {
    mem.pullWaitSince = null;
    return moveTo(p, mem, staging, dangers);
  }
  mem.pullWaitSince ??= p.now;
  // Members running back (e.g. released at the checkpoint) are always waited for.
  if (p.party.some((u) => u.id !== self.id && returning(u, self.pos))) return hold(p, mem);
  // +personas: a member who is AFK or wandered off to a node is waited for (a while).
  if (p.party.some((u) => u.id !== self.id && u.presence) && p.now - mem.pullWaitSince < AWAY_WAIT_MS) return hold(p, mem);
  if (!groupReady(p) && p.now - mem.pullWaitSince < MAX_PULL_WAIT_MS) return hold(p, mem);
  if (roam?.kind === 'wait') return waitClear(p, mem, rc, dangers); // +patrols: let the patrol walk off
  const members = (p.world.packs[rc.pack.id]?.memberIds ?? []).map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive);
  const first = members.reduce<RUnit | undefined>((b, u) => (!b || dist(u.pos, self.pos) < dist(b.pos, self.pos) ? u : b), undefined);
  return moveTo(p, mem, first ? first.pos : rc.center, dangers);
}

/**
 * +patrols: wait for a patrol out of the pack's aggro range: a staging point slid toward the pack
 * (walls) can stand inside it. Back off along the walk from the previous stop until clear.
 */
function waitClear(p: Perception, mem: BotMemory, rc: RouteContext, dangers: readonly DangerArea[]): Command[] {
  const members = (p.world.packs[rc.pack!.id]?.memberIds ?? []).map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive);
  const need = rc.pack!.aggroRadius + 4;
  const clear = (q: Vec2): boolean => members.every((m) => dist(m.pos, q) >= need);
  if (clear(p.self.pos) || !rc.from) return hold(p, mem);
  const path = p.view.queries.spatial.findPath(simWorld(p), p.self.pos, rc.from);
  if (!path) return hold(p, mem);
  let at = p.self.pos;
  for (const wp of path) {
    const n = Math.max(1, Math.ceil(dist(at, wp)));
    for (let k = 1; k <= n; k++) {
      const q = { x: at.x + ((wp.x - at.x) * k) / n, y: at.y + ((wp.y - at.y) * k) / n };
      if (clear(q)) return moveTo(p, mem, q, dangers);
    }
    at = wp;
  }
  return hold(p, mem);
}

/** Far from `to` (> 25 yd) and moving toward it. */
function returning(u: RUnit, to: Vec2): boolean {
  if (dist(u.pos, to) <= 25 || !u.move) return false;
  if (u.move.mode === 'dest') return dist(u.move.dest, to) < dist(u.pos, to) - 5;
  if (u.move.mode === 'dir') {
    const toward = norm(sub(to, u.pos));
    return u.move.dir.x * toward.x + u.move.dir.y * toward.y > 0.3;
  }
  return false;
}

/** Everyone is alive, close, healthy, and the healer has mana. */
export function groupReady(p: Perception): boolean {
  if (p.partyAll.some((u) => !u.alive)) return false;
  for (const u of p.party) {
    if (u.id === p.self.id) continue;
    if (dist(u.pos, p.self.pos) > 25) return false;
    if (u.hp / u.maxHp < 0.7) return false;
    if (u.role === 'healer' && u.resources.mana && u.resources.mana.cur / u.resources.mana.max < 0.6) return false;
  }
  return p.self.hp / p.self.maxHp >= 0.7;
}
